/* eslint eslint-comments/no-use: off */
/* eslint-disable relay/unused-fields */

import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {VALUES} from '@github-ui/item-picker-base/constants/values'
import {type ExtendedItemProps, ItemPicker} from '@github-ui/item-picker-base/ItemPicker'
import {BranchPickerRelay} from '@github-ui/item-picker-branch-relay'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useDebounce} from '@github-ui/use-debounce'
import {GitBranchIcon, TriangleDownIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useFragment, useRelayEnvironment} from 'react-relay'
import {fetchQuery, graphql, readInlineData} from 'relay-runtime'

import type {BranchPickerRef$data, BranchPickerRef$key} from './__generated__/BranchPickerRef.graphql'
import type {BranchPickerRepositoryBranches$key} from './__generated__/BranchPickerRepositoryBranches.graphql'
import type {BranchPickerRepositoryBranchRefs$key} from './__generated__/BranchPickerRepositoryBranchRefs.graphql'
import type {BranchPickerSearchBranchesFragment$key} from './__generated__/BranchPickerSearchBranchesFragment.graphql'
import type {
  BranchPickerSearchBranchesQuery,
  BranchPickerSearchBranchesQuery$data,
} from './__generated__/BranchPickerSearchBranchesQuery.graphql'
import styles from './BranchPicker.module.css'

export type BranchPickerBranch = Omit<BranchPickerRef$data, ' $fragmentType'>

/**
 * Helper function to read BranchPickerRepositoryBranchRefs data from a Relay key
 */
export function readBranchPickerRepositoryBranchRefs(key: BranchPickerRepositoryBranchRefs$key | null) {
  if (!key) return null
  // eslint-disable-next-line no-restricted-syntax
  return readInlineData(BranchPickerRepositoryBranchRefsFragment, key)
}

/**
 * Helper function to read BranchPickerRef data from a Relay key and return as BranchPickerBranch
 */
export function readBranchPickerRef(key: BranchPickerRef$key | null): BranchPickerBranch | null {
  if (!key) return null
  // eslint-disable-next-line no-restricted-syntax
  return readInlineData(BranchPickerRefFragment, key)
}

export function readBranchPickerRepositoryBranches(key: BranchPickerRepositoryBranches$key | null) {
  if (!key) return null
  // eslint-disable-next-line no-restricted-syntax
  return readInlineData(BranchPickerRepositoryBranchesFragment, key)
}

export function BranchPicker(props: BranchPickerInternalProps) {
  const renderMigratedBranchPicker = isFeatureEnabled('item_picker_branch_relay_migration')

  if (renderMigratedBranchPicker) {
    return <BranchPickerRelay {...props} />
  } else {
    return <BranchPickerInternal {...props} />
  }
}

export type BranchPickerInternalProps = {
  initialBranch: BranchPickerBranch | null | undefined
  defaultBranchId: string | undefined
  branches: BranchPickerBranch[] | null
  onSelect: (branch: BranchPickerBranch | undefined) => void
  enforceAtleastOneSelected?: boolean
  owner: string
  repo: string
  title?: string
  'aria-labelledby'?: string
  'aria-describedby'?: string
}

interface BranchPickerBaseProps {
  items: BranchPickerBranch[]
  initialSelectedItem?: BranchPickerBranch | null
  onFilter: (value: string) => void
  defaultBranchId: string | undefined
  onSelectionChange: (selected?: BranchPickerBranch) => void
  loading?: boolean
  title?: string
  'aria-labelledby'?: string
  'aria-describedby'?: string
}

export const BranchPickerRepositoryBranchesGraphqlQuery = graphql`
  query BranchPickerRepositoryBranchesQuery($owner: String!, $name: String!) {
    repository(owner: $owner, name: $name) {
      ...BranchPickerRepositoryBranchRefs
    }
  }
`

const BranchPickerRepositoryBranchRefsFragment = graphql`
  fragment BranchPickerRepositoryBranchRefs on Repository @inline {
    id
    defaultBranchRef {
      ...BranchPickerRef
    }
    refs(refPrefix: "refs/heads/", first: 25) {
      ...BranchPickerRepositoryBranches
    }
  }
`

const BranchPickerRepositoryBranchesFragment = graphql`
  fragment BranchPickerRepositoryBranches on RefConnection @inline {
    edges {
      node {
        ...BranchPickerRef
      }
    }
  }
`

const BranchPickerRefFragment = graphql`
  fragment BranchPickerRef on Ref @inline {
    name
    id
    __typename
    target {
      oid
      id
      __typename
    }
    repository {
      id
      nameWithOwner
      defaultBranchRef {
        name
        id
        target {
          oid
          id
          __typename
        }
        associatedPullRequests {
          totalCount
        }
        repository {
          id
        }
      }
    }
    associatedPullRequests {
      totalCount
    }
  }
`

export const BranchPickerSearchBranches = graphql`
  query BranchPickerSearchBranchesQuery($owner: String!, $name: String!, $first: Int!, $query: String) {
    ...BranchPickerSearchBranchesFragment
  }
`

export const BranchPickerSearchBranchesFragment = graphql`
  fragment BranchPickerSearchBranchesFragment on Query {
    repository(owner: $owner, name: $name) {
      refs(refPrefix: "refs/heads/", first: $first, query: $query) {
        edges {
          node {
            ...BranchPickerRef
          }
        }
      }
    }
  }
`

export const BranchPickerPlaceholder = ({currentBranch}: {currentBranch?: string | null}) => (
  <Button leadingVisual={GitBranchIcon} trailingVisual={TriangleDownIcon} disabled>
    {currentBranch ?? LABELS.selectBranch}
  </Button>
)

export function BranchPickerInternal({
  initialBranch,
  defaultBranchId,
  branches: inputBranches,
  owner,
  repo,
  onSelect,
  title,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}: BranchPickerInternalProps) {
  const [searchResultsFragment, setSearchResultsFragment] = useState<BranchPickerSearchBranchesQuery$data | null>(null)
  const [searchResults, setSearchResults] = useState<BranchPickerBranch[] | undefined>(undefined)
  const [searchLoading, setSearchLoading] = useState<boolean>(false)
  const [filter, setFilter] = useState('')
  const {addToast} = useToastContext()

  const searchedBranchesData = useFragment<BranchPickerSearchBranchesFragment$key>(
    BranchPickerSearchBranchesFragment,
    searchResultsFragment,
  )

  const suggestedBranches = useMemo(() => {
    if (!searchedBranchesData) {
      return undefined
    }
    return (searchedBranchesData?.repository?.refs?.edges || []).flatMap(edge =>
      // eslint-disable-next-line no-restricted-syntax
      edge?.node ? [readInlineData<BranchPickerRef$key>(BranchPickerRefFragment, edge.node)] : [],
    )
  }, [searchedBranchesData])

  useEffect(() => {
    setSearchResults(suggestedBranches)
  }, [suggestedBranches])

  const branches = useMemo(() => {
    const repoBranches =
      (inputBranches || [])

        .filter((branch): branch is BranchPickerBranch => Boolean(branch))
        .filter(branch => branch && branch.id !== initialBranch?.id) ?? []

    if (initialBranch) {
      repoBranches.unshift(initialBranch)
    }
    return repoBranches
  }, [inputBranches, initialBranch])

  const items = useMemo(() => {
    if (searchResults) {
      return searchResults
    }
    return branches
  }, [branches, searchResults])

  const environment = useRelayEnvironment()

  const fetchSearchData = useCallback(
    (query: string) => {
      if (!query) {
        setSearchResults(undefined)
        return
      }

      setSearchLoading(true)

      fetchQuery<BranchPickerSearchBranchesQuery>(environment, BranchPickerSearchBranches, {
        owner,
        name: repo,
        query,
        first: 25,
      }).subscribe({
        next: (data: BranchPickerSearchBranchesQuery$data) => {
          if (data !== null) {
            setSearchResultsFragment(data)
          }
          setSearchLoading(false)
        },
        error: () => {
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: ERRORS.couldNotSearchAssignees,
          })
          setSearchLoading(false)
        },
      })
    },
    [addToast, environment, owner, repo],
  )

  const debounceFetchSearchData = useDebounce(
    (nextValue: string) => fetchSearchData(nextValue),
    VALUES.pickerDebounceTime,
  )

  const filterItems = useCallback(
    (value: string) => {
      const trimmedFilter = value.trim()
      if (filter === trimmedFilter) return

      debounceFetchSearchData(trimmedFilter)
      setFilter(value)
    },
    [debounceFetchSearchData, filter],
  )

  return (
    <BranchPickerBase
      items={items}
      initialSelectedItem={initialBranch}
      onFilter={filterItems}
      defaultBranchId={defaultBranchId}
      onSelectionChange={onSelect}
      loading={searchLoading}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      title={title}
    />
  )
}

export function BranchPickerBase({
  items,
  initialSelectedItem,
  onFilter,
  defaultBranchId,
  onSelectionChange,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  title,
  loading = false,
}: BranchPickerBaseProps) {
  const filterItems = useCallback(
    (value: string) => {
      onFilter(value)
    },
    [onFilter],
  )

  const getItemKey = useCallback((branch: BranchPickerBranch) => branch.id, [])

  const convertToItemProps = useCallback(
    (branch: BranchPickerBranch): ExtendedItemProps<BranchPickerBranch> => ({
      // this is a hack to make sure that we are using the prop
      id: `${branch.id}`,
      children: <span>{branch.name}</span>,
      source: branch,
      leadingVisual: () => <Octicon icon={GitBranchIcon} size={16} />,
      trailingVisual: () => (branch.id === defaultBranchId ? <span>Default</span> : null),
    }),
    [defaultBranchId],
  )

  const branchPickerRef = useRef<HTMLButtonElement>(null)

  const renderAnchor = useCallback(
    ({...anchorProps}: React.HTMLAttributes<HTMLElement>) => (
      <Button
        leadingVisual={GitBranchIcon}
        trailingVisual={TriangleDownIcon}
        aria-label={!ariaLabelledBy ? LABELS.selectBranch : undefined}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        {...anchorProps}
        ref={branchPickerRef}
      >
        {initialSelectedItem ? <span>{initialSelectedItem.name}</span> : LABELS.selectBranch}
      </Button>
    ),
    [ariaDescribedBy, ariaLabelledBy, initialSelectedItem],
  )

  const showNoMatchMessage = useMemo(() => items.length === 0, [items])

  return (
    <div className={styles.branchPickerContainer}>
      <ItemPicker
        items={items}
        initialSelectedItems={initialSelectedItem ? [initialSelectedItem] : []}
        filterItems={filterItems}
        getItemKey={getItemKey}
        convertToItemProps={convertToItemProps}
        placeholderText={LABELS.selectBranch}
        selectionVariant="single"
        onSelectionChange={([branch]) => onSelectionChange(branch)}
        loading={loading}
        renderAnchor={renderAnchor}
        selectPanelRef={branchPickerRef}
        enforceAtleastOneSelected
        resultListAriaLabel={'Branch results'}
        title={title ?? 'Select a branch'}
        width={'large'}
        improvedNoMatchAccessibility={showNoMatchMessage}
        noMatchMessage={
          showNoMatchMessage
            ? {
                title: 'No branches were found',
                variant: 'empty',
                body: 'Try searching with a different query for results.',
              }
            : undefined
        }
      />
    </div>
  )
}

try{ BranchPicker.displayName ||= 'BranchPicker' } catch {}
try{ BranchPickerPlaceholder.displayName ||= 'BranchPickerPlaceholder' } catch {}
try{ BranchPickerInternal.displayName ||= 'BranchPickerInternal' } catch {}
try{ BranchPickerBase.displayName ||= 'BranchPickerBase' } catch {}